import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Box,
    Typography,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    IconButton,
    Tooltip,
    Grid,
    Divider,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

//Standard Api
import axios from "axios";

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { createApiClient } from "../../components/useApiClient";
import { useSafeRequest } from "../../components/useSafeRequest";
import { useIsAuthenticated } from "@azure/msal-react";
interface Artwork {
    artworkId: number;
    title: string;
    artistName: string;
    latestValue?: number;
    imageUrls?: string[];
}

interface ArtworkImage {
    imageId?: number;
    artworkId?: number;
    imageUrl: string;
    imageCaption?: string;
    isPrimary?: boolean;
    uploadedAt?: string;
}
interface Portfolio {
    portfolioId: number | null;
    name: string;
    description?: string;
    createdDate: string;
    artworks: Artwork[];
}

export default function PortfolioDetail({
    open, onClose, onEdit,
    portfolioId,
}: {
        open: boolean;
        onClose: () => void;
        onEdit: () => void;
        portfolioId: number | null;
    }) {

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    //Support API Calls
    const [isLoading, setIsLoading] = useState(false);
    const { activeClientId, getAccessToken, user } = useUser();
    const previousClientId = useRef<number | null>(activeClientId);

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const isAuthenticated = useIsAuthenticated();

    // *** Setup safe API Calls ***
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadPortfolio();
    }, []);

    //API Calls
    const portfolioReq = useSafeRequest<any>(api, { maxAttempts: 3 });

    const loadPortfolio = async () => {

        console.info("Load portfolio");

        if (portfolioId) {
            setLoading(true);

            try {
                const tasks = [
                    portfolioReq.run({ method: "GET", url: `portfolios/${portfolioId}` }),
                ];

                const [a] = await Promise.allSettled(tasks);

                if (a.status === "fulfilled") setPortfolio(a.value);

                console.info("Portfolio loaded");
            }
            catch (error) {
                console.error('Failed to load portfolio data:', error);
            } finally {
                //Loading complete
                console.info("Load portfolio complete");
                setLoading(false);
            }
        }
    };

    const totalValue = useMemo(() => {
        if (!portfolio) return;

        const artworks = portfolio?.artworks;

        return artworks?.reduce(
            (sum, a) => sum + (a.latestValue || 0),
            0
        ) ?? 0;
    }, [portfolio]);

    const handleRemove = async (artworkId: number) => {
        if (!window.confirm("Remove artwork from this portfolio?")) return;

        const accessToken = await getAccessToken();

        if (accessToken) {
            const response = await axios.delete(
                `${BASE_URL}/portfolios/${portfolioId}/artworks/${artworkId}`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'X-Client-Id': activeClientId?.toString() || '',
                        "Content-Type": "application/json"
                    }
                });
        }

        loadPortfolio();
    };

    if (loading) return <Typography>Loading portfolio...</Typography>;
    if (!portfolio) return <Typography>Portfolio not found.</Typography>;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{portfolio ? "Edit Portfolio" : "Create Portfolio"}</DialogTitle>
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Tooltip title="Back">
                    <IconButton onClick={onClose} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {portfolio.name}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={onEdit}
                >
                    Edit
                </Button>
            </Box>

            {portfolio.description && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {portfolio.description}
                </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Artworks ({portfolio.artworks.length}) — Total Value:{" "}
                <strong>
                    £{totalValue?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </strong>
            </Typography>

            <Grid container spacing={2}>
                    {portfolio.artworks.map((art) => (

                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={art.artworkId}>
                        <Card
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                position: "relative",
                            }}
                        >
                            {art.imageUrls && art.imageUrls.length > 0 ? (
                                <CardMedia
                                    sx={{
                                        objectFit: "cover",
                                        borderBottom: "1px solid #eee",
                                        backgroundColor: "#f7f7f7",
                                    }}
                                    component="img"
                                    height="180"
                                        image={art.imageUrls[0] || "/images/placeholder.gif"}
                                    alt={art.title}
                                />
                            ) : (
                                    <div className="no-images">
                                        <div className="no-images-icon">🖼️</div>
                                        <p>No images available</p>
                                    </div>
                            )}
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                    {art.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {art.artistName}
                                </Typography>
                                {art.latestValue && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1 }}
                                    >
                                        £{art.latestValue.toLocaleString()}
                                    </Typography>
                                )}
                            </CardContent>
                            <CardActions sx={{ justifyContent: "flex-end" }}>
                                <Tooltip title="Remove from portfolio">
                                    <IconButton onClick={() => handleRemove(art.artworkId)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {portfolio.artworks.length === 0 && (
                <Typography color="text.secondary" sx={{ mt: 3 }}>
                    No artworks in this portfolio yet.
                </Typography>
            )}
            </Box>
        </Dialog>
    );
}
